import * as Sentry from "@sentry/react"
import { useEffect } from "react"

import { startCaptureRecovery } from "@/lib/drafts/captureRecovery"
import { pruneStaleDrafts } from "@/lib/drafts/draftsDb"
import { initStorageHealth } from "@/lib/storageHealth"
import { appLifecycle } from "@/platform"
import { toast } from "@fieldbook/ui/toaster"

/**
 * Startup housekeeping for the things that only make sense once per app load:
 * reporting a WebView renderer loss, clearing stale recovery records, and
 * picking up a capture the OS held while we were dead.
 *
 * Renders nothing; mounted once at the router root.
 */
export function AppRecovery() {
  // A renderer loss takes the JS context with it, so @sentry/react can never
  // report it from the inside. The native shell leaves a breadcrumb instead
  // and we pick it up here, on the next run.
  useEffect(() => {
    let cancelled = false

    void appLifecycle.consumeCrashReport().then((report) => {
      if (cancelled || !report) return

      Sentry.captureMessage("WebView renderer process gone", {
        level: "error",
        tags: { recovery: "render_process_gone" },
        extra: {
          didCrash: report.didCrash,
          occurredAt: new Date(report.timestamp).toISOString(),
        },
      })

      toast.warning(
        "Fieldbook restarted after running out of memory. Any unsaved form will be restored when you reopen it.",
      )
    })

    return () => {
      cancelled = true
    }
  }, [])

  // Temporary recovery records hold photo bytes, so stale ones are the
  // heaviest thing we leave lying around in IndexedDB. User-saved drafts are
  // excluded from pruning.
  useEffect(() => {
    void pruneStaleDrafts().then((removed) => {
      if (removed > 0) console.info(`[Drafts] Pruned ${removed} stale draft(s)`)
    })
  }, [])

  // A capture the OS held for us while we were dead is reattached to the draft
  // that was waiting on it. See lib/drafts/captureRecovery.ts — forms wait on
  // that before reading their own draft, so this cannot be raced.
  useEffect(() => {
    return startCaptureRecovery((outcome) => {
      if (outcome === "recovered") {
        toast.success(
          "Recovered the photo you took before Fieldbook restarted.",
        )
      } else if (outcome === "failed") {
        toast.warning(
          "A photo was taken before Fieldbook restarted but couldn't be recovered — please take it again.",
        )
      }
    })
  }, [])

  // initStorageHealth() is already running by the time this mounts (main.tsx
  // kicks it off right after Sentry.init) — this just waits on the same
  // memoized result to decide whether the surveyor needs a heads-up. Doing it
  // here rather than in main.tsx ensures <Toaster/> is mounted first.
  useEffect(() => {
    let cancelled = false

    void initStorageHealth().then((status) => {
      if (cancelled || status.level === "ok") return

      if (status.level === "critical") {
        toast.warning(
          "Device storage is critically low. Unsynced records and photos could be lost — sync now if you have reception, and free up space as soon as you can.",
          { duration: 10000 },
        )
      } else {
        toast.warning(
          "Device storage is running low. Sync when you have reception and free up space to avoid losing unsynced records or photos.",
        )
      }
    })

    return () => {
      cancelled = true
    }
  }, [])

  return null
}
